import { useState } from 'react';
import { X, CreditCard } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { LEGAL_URLS } from '../config/legal';
import { recordBillingAck } from '../lib/billingConsent';

interface ChargeConfirmModalProps {
  projectId: string;
  cardBrand: string | null;
  cardLast4: string | null;
  onSuccess: () => void;
  onCancel: () => void;
}

function formatCardBrand(brand: string | null): string {
  if (!brand) return 'card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

export function ChargeConfirmModal({
  projectId,
  cardBrand,
  cardLast4,
  onSuccess,
  onCancel,
}: ChargeConfirmModalProps) {
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    if (!agreed) return;
    setLoading(true);
    setError(null);

    const ack = await recordBillingAck();
    if (!ack.ok) {
      setError(ack.error || 'Could not record your acknowledgment. Please try again.');
      setLoading(false);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch('/.netlify/functions/charge-saved-method', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ project_id: projectId }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Payment failed. Please update your payment method in account settings.');
        setLoading(false);
        return;
      }

      onSuccess();
    } catch {
      setError('Payment failed. Please update your payment method in account settings.');
      setLoading(false);
    }
  }

  const cardDisplay = cardLast4
    ? `${formatCardBrand(cardBrand)} ••••${cardLast4}`
    : 'your saved card';

  return (
    <div data-testid="charge-confirm-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Confirm Purchase</h2>
          <button
            onClick={onCancel}
            disabled={loading}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Cancel"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">
              Unlock Results for this project?{' '}
              <span className="font-semibold text-gray-900">$30.00</span> will be charged
              to {cardDisplay}. Results will be permanently available for this project.
            </p>
          </div>

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="charge-consent-agree"
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
            />
            <span className="text-sm text-gray-600 leading-snug">
              I authorize a one-time $30.00 charge to my payment method and agree to the{' '}
              <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Terms of Service</a>.
            </span>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-5 pt-0">
          <button
            data-testid="btn-charge-cancel"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid="btn-charge-confirm"
            onClick={handleConfirm}
            disabled={loading || !agreed}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Processing...
              </>
            ) : (
              'Confirm — $30.00'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
